import request from '@/util/request'
import _ from 'lodash'
import {optionsToPaginationParams} from '@/util/pagination'
import {default as productMixin} from '@/mixins/product'
import { EVENT_PROVIDERS, ANALYTICS_EVENTS } from '@/util/constants'
import { sendEvent } from '@/mixins/eventTransmitter'

const state = {
  categories: [],
  categoriesTree: [],
  products: {
    data: {
      count: null,
      rows: [],
    },
    page: null,
    nextPage: null,
  },
  productDetail: {
    id: null,
  },
}

const getters = {
  getCategories: (state) => state.categories,
  getCategoriesTree: (state) => state.categoriesTree,
  getProducts: (state) => state.products,
  getProductDetail: (state) => state.productDetail,
  getProductById: (state) => (id) => {
    if (state.productDetail.id === id) return state.productDetail
    const match = state.products.data.rows.filter(
      (product) => product.id === id
    )
    return match.length === 1 ? match[0] : null
  },
}

/**
 *
 * @param getters
 * @param commit
 * @param productId
 * @param field
 * @param value
 */
const updateProductState = (
  {getters, commit},
  {productId, field, value}
) => {
  let change = false
  const rows = getters.getProducts.data.rows.map((product) => {
    if (product.id === productId) {
      change = true
      product[field] = value
    }
    return product
  })
  if (change) commit('SET_PRODUCT_ROWS', rows)
  if (getters.getProductDetail.id === productId) {
    const productDetail = getters.getProductDetail
    productDetail[field] = value
    commit('SET_PRODUCT_DETAIL', productDetail)
  }
}

const actions = {
  async fetchSpotByTargetPrice(
    {getters},
    {quantity, targetPrice, bidAsk, productId}
  ) {
    return request({
      url: `/products/target-spot/${productId}/${getters.getSelectedCompany.id}`,
      params: {
        quantity,
        targetPrice,
        bidAsk,
      },
      method: 'post',
    })
  },
  async fetchProductWithQuantity(
    {getters, dispatch, commit},
    {id, quantity}
  ) {
    const query = JSON.stringify({
      id,
    })
    return request({
      url: '/products',
      params: {
        companyId: getters.getSelectedCompany.id,
        query,
        quantities: JSON.stringify({[id]: quantity}),
        limit: 10000,
        offset: 0,
      },
      method: 'get',
    }).then((data) => _.get(data, `data.rows[0]`, {}))
  },
  async createProductStockAlert(
    {dispatch, getters, commit, rootState, rootGetters},
    {item, buySell}
  ) {
    const res = await request({
      url: '/product-stock-alerts',
      method: 'post',
      data: {
        userId: getters.getUserId,
        productId: item.id,
        transactionType: buySell,
      },
    })
    await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
      eventName: ANALYTICS_EVENTS.STOCK_ALERT,
      item_id: item.netsuite_internalId,
      item_name: item.name,
    }, { getters: rootGetters, dispatch })
    // update local product state so UI reflects alert immediately
    const productId = item.id
    const field = 'productStockAlert'
    const currentProduct = getters.getProductById(productId) || {}
    const current = _.get(currentProduct, 'productStockAlert', [])
    const record = typeof res === 'object' ? res : { transactionType: buySell }
    const value = current
      .filter((x) => x.transactionType !== buySell)
      .concat([record])
    updateProductState({getters, commit}, {productId, field, value})
    return res
  },
  async cancelProductStockAlert(
    {dispatch, getters, commit, rootState, rootGetters},
    {item, buySell}
  ) {
    const res = await request({
      url: '/product-stock-alerts',
      method: 'delete',
      data: {
        userId: getters.getUserId,
        productId: item.id,
        transactionType: buySell,
      },
    })
    await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
      eventName: ANALYTICS_EVENTS.STOCK_ALERT,
      item_id: item.netsuite_internalId,
      item_name: item.name,
      cancelled: true
    }, { getters: rootGetters, dispatch })
    // update local product state so UI reflects alert removal immediately
    const productId = item.id
    const field = 'productStockAlert'
    const currentProduct = getters.getProductById(productId) || {}
    const current = _.get(currentProduct, 'productStockAlert', [])
    const value = current.filter((x) => x.transactionType !== buySell)
    updateProductState({getters, commit}, {productId, field, value})
    return res
  },
  async markAsFavorite({dispatch, getters, commit, rootState, rootGetters}, item) {
    const ADD_OR_REMOVE = item.productFavourite && item.productFavourite.length ? ANALYTICS_EVENTS.FAV_REMOVE : ANALYTICS_EVENTS.FAV_ADD
    const result = (ADD_OR_REMOVE === ANALYTICS_EVENTS.FAV_REMOVE)
        ? await request({
          url: `/products/favourite/${item.id}/${getters.getSelectedCompany.id}`,
          method: 'delete',
        })
        : await request({
          url: `/products/favourite/${item.id}/${getters.getSelectedCompany.id}`,
          method: 'post',
        })
    const productId = item.id
    const field = `productFavourite`
    const value =
      typeof result === `object` ? [result] : []
    updateProductState({getters, commit}, {productId, field, value})
    // analytics
    await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
      eventName: ADD_OR_REMOVE,
      item_id: item.netsuite_internalId,
      item_name: item.name,
    }, { getters: rootGetters, dispatch })
    return result
  },
  async updatePositions({dispatch, getters}, update) {
    return request({
      url: `/products/ordering`,
      method: 'put',
      data: update,
    })
  },
  async updateCategoryNotes({commit}, {categoryId, adminNotes}) {
    return request({
      url: '/catalogs/categories',
      method: 'put',
      data: [{
        id: categoryId,
        adminNotes: adminNotes
      }]
    });
  },
  fetchCategories({getters, commit}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/catalogs/categories/${companyId}?limit=10000`,
      method: 'get',
    }).then((response) => {
      commit('SET_CATEGORIES', response.data.rows)
      commit('SET_CATEGORIES_TREE', _.clone(response.data.rows))
    })
  },
  updateCategoriesOrder({commit}, categories) {
    return request({
      url: '/catalogs/categories',
      method: 'put',
      data: categories,
    }).then((response) => {
      commit('SET_CATEGORIES', response)
      commit('SET_CATEGORIES_TREE', _.clone(response))
    })
  },
  async fetchProducts({getters, commit, dispatch}, {options, query}) {
    const paginationParams = optionsToPaginationParams(options)
    const companyId = getters.getSelectedCompany.id
    return request({
      url: '/products',
      params: {companyId, query, ...paginationParams},
      method: 'get',
    }).then((data) => {
      commit('SET_PRODUCTS', data)
    })
  },
  async fetchProductById({getters, commit}, id) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: '/products/' + id,
      params: {companyId},
      method: 'get',
    }).then((data) => {
      commit('SET_PRODUCT_DETAIL', data)
      return data
    })
  },
  async updateMarkups({getters, commit}, {id, data}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: '/products/markups/' + id + '/' + companyId,
      method: 'put',
      data,
    }).then((data) => {
      return data
    })
  },
  async updateProductField(_, data) {
    return request({
      url: '/products', 
      method: 'patch',
      data,
    }).then((data) => {
      return data
    })
  },
}

const mutations = {
  /**
   * Set Products
   * @param state
   * @param products
   * @constructor
   */
  SET_PRODUCTS(state, products) {
    state.products = products
  },
  /**
   * Set Products
   * @param state
   * @param products
   * @constructor
   */
  SET_PRODUCT_ROWS(state, rows) {
    state.products.data.rows = rows
  },
  /**
   * Set Product Detail
   * @param state
   * @param product
   * @constructor
   */
  SET_PRODUCT_DETAIL(state, product) {
    state.productDetail = product
  },
  /**
   * Set Categories
   * @param state
   * @param categories
   * @constructor
   */
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  /**
   * Clever bit of code that converts array of categories into tree structure (without use of recursion)
   * @param state
   * @param categories
   * @constructor
   */
  SET_CATEGORIES_TREE(state, categories) {
    state.categoriesTree = productMixin.methods.treeSort(categories)
  },
  UPDATE_CATEGORY_NOTES(state, {categoryId, adminNotes}) {
    const category = state.categories.find(cat => cat.id === categoryId);
    if (category) {
      category.adminNotes = adminNotes;
    }
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}
